import { AddButton, CloseButton, SaveButton } from '@ui/shared/Button';
import reject from 'lodash/reject';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  closePopup,
  openPopup,
} from '../../../../data/providers/popupsProvider';
import { userAsPromise } from '../../../../data/providers/userProvider';
import { IAthlete, IAthleteCSVRow, IGroup, ITeam, IUser } from '../../../../data/types';
import {
  parseExternalProperties,
  saveAthletes,
} from '../../../../data/writers/athletes';
import { parseHeightToCentimeters, parseDateToISO } from '../../../../utils/conversions';
import { useEntities } from '../../../../hooks/useEntities';
import { Icon } from '../../../shared/Icon';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import { PopupCard, PopupCardContent } from '../../../shared/popups/PopupCard';
import { TabLabel, TabLabels } from '../../../shared/tabs/Tabs';
import { AthleteForm } from './AthleteForm';
import { Import } from './import/Import';
import { TAB_STATE } from './tab_state';
import {
  ErrorBanner,
  IErrorState,
  useDuplicateLookup,
  useErrorLookup,
  useFormError,
} from './validation';

interface IPopupProps {
  closeDialog: () => void;
}

const Popup = (props: IPopupProps): React.ReactElement => {
  const [activeTab, setActiveTab] = React.useState<TAB_STATE>(TAB_STATE.SINGLE);
  const [athlete, setAthlete] = React.useState<Partial<IAthlete>>({});
  const [importList, setImportList] = React.useState<
    Array<Partial<Partial<IAthlete>>>
  >([]);
  const [selectedGroups, setSelectedGroups] = React.useState<IGroup[]>([]);
  const [selectedTeams, setSelectedTeams] = React.useState<ITeam[]>([]);
  const [saving, setSaving] = React.useState<boolean>(false);
  /// subscribe to entities
  const { athletes, teams, groups, loading } = useEntities();

  const [error, setError] = React.useState<IErrorState>({ active: false });

  const [user, setUser] = React.useState<IUser>();

  /**
   * @description get the org id
   */
  React.useEffect((): void => {
    userAsPromise().then(setUser);
  }, []);

  const existingDuplicateLookup = useDuplicateLookup(athletes);

  const existingErrorLookup = useErrorLookup(athlete, existingDuplicateLookup);

  /**
   * @description handles changes to the athlete
   * @param name
   * @param value
   */
  const onAthleteChange = (name: string, value: string): void => {
    const externalProperties = parseExternalProperties({ [name]: value });
    let changedAthlete: Partial<Partial<IAthlete>>;

    if (externalProperties) {
      // If name is 'external', KeyValueControl is sending all properties - replace completely
      // If name is 'external:*', it's a single field update - merge with existing
      changedAthlete = {
        ...athlete,
        external:
          name === 'external'
            ? { ...externalProperties }
            : { ...athlete?.external, ...externalProperties },
      };
    } else {
      changedAthlete = {
        ...athlete,
        [name]: value,
      };
    }

    setAthlete(changedAthlete);
  };

  /**
   * @description handles image change
   * @param mAthlete
   */
  const onImageChange = (mAthlete: Partial<Partial<IAthlete>>): void => {
    setAthlete(mAthlete);
  };

  /**
   * @description handles import of csv list
   * @param list
   */
  const addImportList = async (list: IAthleteCSVRow[]): Promise<void> => {
    const sanitizedAthletes = list.map(
      (ath: IAthleteCSVRow): Partial<IAthlete> => {
        const newAthlete: Partial<IAthlete> = {
          name: ath.name,
          position: ath.position || 'NA',
          image: ath.image ?? null,
          groups: selectedGroups.map((g: IGroup): string => g.id),
          teams: selectedTeams.map((t: ITeam): string => t.id),
          external: null,
        };
        if (ath.image && ath.image !== '') {
          newAthlete.image = ath.image;
        }
        // Map sport field
        if (ath.sport) {
          newAthlete.sport = ath.sport;
        }
        // Map and parse dob field (supports various date formats)
        if (ath.dob) {
          const parsedDob = parseDateToISO(String(ath.dob));
          if (parsedDob) {
            newAthlete.dob = parsedDob;
          }
        }
        // Parse and map height field (supports cm, in, ft+in formats)
        const parsedHeight = parseHeightToCentimeters(ath.height);
        if (parsedHeight !== undefined) {
          newAthlete.height = parsedHeight;
        }
        const externalProperties = parseExternalProperties(ath);
        if (externalProperties) {
          newAthlete.external = externalProperties;
        }
        return newAthlete;
      }
    );
    const newAthleteList = reject(
      sanitizedAthletes,
      (item: IAthlete): boolean =>
        existingDuplicateLookup.name.has(item.name) ||
        (item.external?.uniqueId
          ? existingDuplicateLookup.uniqueId.has(item.external.uniqueId)
          : false)
    );
    setImportList(newAthleteList);
  };

  /**
   * @description handles the user saving the athlete(s)
   */
  const saveAthlete = (): void => {
    setSaving(true);
    setError({ active: false });
    const mAthletes =
      activeTab === TAB_STATE.SINGLE
        ? [
            {
              ...athlete,
              teams: selectedTeams.map((t: ITeam): string => t.id),
              groups: selectedGroups.map((g: IGroup): string => g.id),
            },
          ]
        : importList.map(
            (mAthlete: Partial<IAthlete>): Partial<Partial<IAthlete>> => {
              return {
                ...mAthlete,
                position: mAthlete.position ? mAthlete.position : 'NA',
                name: mAthlete.name,
                image: mAthlete.image,
                groups: selectedGroups.map((g: IGroup): string => g.id),
                teams: selectedTeams.map((t: ITeam): string => t.id),
              };
            }
          );

    saveAthletes(mAthletes)
      .then((): void => props.closeDialog())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .catch((e: any): void => {
        setSaving(false);
        setError({
          active: true,
          message: e?.details?.reason ?? e.message,
          items: e?.details?.invalidAthletes ?? [],
        });
      });
  };

  /**
   * @description selects all the available athletes
   */
  const selectAllGroups = (): void =>
    setSelectedGroups(groupsAvailableToSelect);
  /**
   * @description clears the currently selected athletes
   */
  const clearAllGroups = (): void => setSelectedGroups([]);

  /**
   * @description selects all the available teams
   */
  const selectAllTeams = (): void => setSelectedTeams(teams);

  /**
   * @description clears the currently selected teams
   */
  const clearAllTeams = (): void => setSelectedTeams([]);

  /**
   * @description handles the selection/de-selection of a team
   * @param id
   * @param checked
   */
  const teamSelectionHandler = (team: ITeam, checked: boolean): void => {
    let mSelectedTeams = [...selectedTeams];
    if (checked) {
      mSelectedTeams.push(team);
    } else {
      mSelectedTeams = mSelectedTeams.filter(
        (t: ITeam): boolean => t.id !== team.id
      );
    }
    setSelectedTeams(mSelectedTeams);
  };

  /**
   * @description handles the selection/de-selection of a group
   * @param id
   * @param checked
   */
  const groupSelectionHandler = (group: IGroup, checked: boolean): void => {
    let mSelectedGroups = [...selectedGroups];
    if (checked) {
      mSelectedGroups.push(group);
    } else {
      mSelectedGroups = mSelectedGroups.filter(
        (g: IGroup): boolean => g.id !== group.id
      );
    }
    setSelectedGroups(mSelectedGroups);
  };

  const hasFormError = useFormError({
    activeTab,
    importList,
    athlete,
    user,
    selectedTeams,
  });

  /**
   * @description the list of athletes that are available to select because
   *	they are members of at least one selected team
   */
  const groupsAvailableToSelect: IGroup[] = React.useMemo((): IAthlete[] => {
    return groups.filter((g: IGroup): boolean =>
      g.teams?.some((id: string): boolean =>
        selectedTeams.map((t: ITeam): string => t.id).includes(id)
      )
    );
  }, [groups?.length, selectedTeams]);

  return (
    <PopupCard onClose={props.closeDialog} size='large' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={6} noPadding borderless>
          <CardHeaderLeft>
            <div
              style={{
                padding: '10px',
              }}
            >
              <h5>Add Athletes</h5>
            </div>
          </CardHeaderLeft>
          <CardHeaderRight
            style={{
              flex: 2,
            }}
          >
            <TabLabels>
              <TabLabel
                activeElevation={4}
                onClick={(): void => setActiveTab(TAB_STATE.SINGLE)}
                selected={TAB_STATE.SINGLE === activeTab}
              >
                <Icon name='person' color={TAB_STATE.SINGLE === activeTab ? 'var(--color-warning-accent)' : undefined} /> Individual
              </TabLabel>
              <TabLabel
                activeElevation={4}
                onClick={(): void => setActiveTab(TAB_STATE.IMPORT)}
                selected={TAB_STATE.IMPORT === activeTab}
              >
                <Icon name='playlist_add' color={TAB_STATE.IMPORT === activeTab ? 'var(--color-warning-accent)' : undefined} /> Import
              </TabLabel>
            </TabLabels>
            <div
              style={{
                padding: '10px',
              }}
            >
              <CloseButton onClick={props.closeDialog} />
            </div>
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {(saving || loading) && <LinearLoading />}
          {error.active && <ErrorBanner message={error?.message} />}
          {activeTab === TAB_STATE.SINGLE ? (
            <AthleteForm
              athlete={athlete}
              athletes={athletes}
              teams={teams}
              selectedTeams={selectedTeams}
              selectedGroups={selectedGroups}
              teamSelectionHandler={teamSelectionHandler}
              groups={groups}
              groupSelectionHandler={groupSelectionHandler}
              onChange={onAthleteChange}
              onImageChange={onImageChange}
              errorLookup={existingErrorLookup}
              clearAllTeams={clearAllTeams}
              selectAllTeams={selectAllTeams}
              clearAllGroups={clearAllGroups}
              selectAllGroups={selectAllGroups}
              enforceUniqueAthleteId={user?.orgOptions?.enforceUniqueAthleteId}
            />
          ) : (
            <Import
              onError={setError}
              onAthletesAdded={addImportList}
              onImportListUpdated={setImportList}
              importList={importList}
              selectedTeams={selectedTeams}
              selectedGroups={selectedGroups}
              groups={groups}
              teams={teams}
              groupSelectionHandler={groupSelectionHandler}
              teamSelectionHandler={teamSelectionHandler}
              errorItems={error?.items}
            />
          )}
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft />
          <CardFooterRight>
            <SaveButton
              onClick={saveAthlete}
              type='submit'
              disabled={saving || hasFormError}
            />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </PopupCard>
  );
};

const AddAthletesPopup = withRouter(
  (props: RouteComponentProps): React.ReactElement => {
    const openDialog = (): void =>
      openPopup(
        <Popup closeDialog={closePopup(props.history)} />,
        props.history,
        'athletes-add'
      );

    return <AddButton onClick={openDialog} />;
  }
);

export { AddAthletesPopup };
